import React, { useEffect, useRef } from "react";

// Виджет Cloudflare Turnstile («Security check») для входа и регистрации.
//
// Скрипт Cloudflare грузится один раз и только на экране входа: остальной
// панели он не нужен. Виджет рисуется явно (render=explicit), чтобы при смене
// формы или после попытки входа его можно было пересоздать — токен одноразовый,
// второй раз сервер его не примет. Пересоздание — сменой resetKey.
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else {
        scriptPromise = null;
        reject(new Error("turnstile-missing"));
      }
    };
    script.onerror = () => {
      // Следующая попытка (например, после отключения блокировщика) загрузит заново.
      scriptPromise = null;
      script.remove();
      reject(new Error("turnstile-script-failed"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

const TurnstileWidget = ({ siteKey, action, language = "auto", resetKey = 0, onToken, onError }) => {
  const containerRef = useRef(null);
  // Обработчики — через ссылки: пересоздавать виджет из-за новой функции на
  // каждой отрисовке формы значило бы сбрасывать уже пройденную проверку.
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    let widgetId = null;
    onTokenRef.current?.("");
    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "dark",
          size: "flexible",
          language,
          callback: (token) => onTokenRef.current?.(String(token || "")),
          "expired-callback": () => onTokenRef.current?.(""),
          "timeout-callback": () => onTokenRef.current?.(""),
          "error-callback": (code) => {
            onTokenRef.current?.("");
            onErrorRef.current?.(String(code || "widget"));
          }
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.("script");
      });
    return () => {
      cancelled = true;
      if (widgetId !== null && typeof window !== "undefined" && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (_) {
          // виджет уже снят вместе с контейнером
        }
      }
    };
  }, [siteKey, action, language, resetKey]);

  return <div ref={containerRef} className="auth-turnstile" />;
};

export default TurnstileWidget;
